import React, { useEffect, useRef, useState } from 'react';
import { spacing } from '../../elements/spacing';
import { SkeletonBlock } from '../SkeletonBlock/SkeletonBlock.web';
import { Text } from '../Text/Text.web';
import type { SkeletonTextProps } from './SkeletonText';

type Box = Pick<DOMRect, 'x' | 'y' | 'width' | 'height'>;
type WebProps = Partial<SkeletonTextProps> & { 'data-testid'?: string };
export function SkeletonText({
  text,
  variant = 'b1Regular',
  shape = 'lines',
  lines = 1,
  width = 'fill',
  active = true,
  testID,
  'data-testid': dataTestId,
}: WebProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const id = dataTestId ?? testID;
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => {
      const bounds = node.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) {
        setBoxes([]);
        return;
      }
      const span = node.querySelector('span');
      const rects = text && span ? Array.from(span.getClientRects()) : [];
      setBoxes(
        shape === 'block'
          ? [{ x: 0, y: 0, width: bounds.width, height: bounds.height }]
          : rects.length > 0
            ? rects.map((r) => ({
                x: r.x - bounds.x,
                y: r.y - bounds.y,
                width: r.width,
                height: r.height,
              }))
            : Array.from({ length: lines }, (_, index) => ({
                x: 0,
                y: (index * bounds.height) / lines,
                width: bounds.width,
                height: bounds.height / lines,
              })),
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, variant, lines, width, shape]);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid={id}
      style={{ width: width === 'fill' ? '100%' : width, position: 'relative' }}
    >
      <div style={{ visibility: 'hidden', whiteSpace: 'pre-wrap' }}>
        <Text
          value={text || Array.from({ length: lines }, () => '\u2007').join('\n')}
          variant={variant}
          accessible={false}
          data-testid={id ? `${id}-geometry` : undefined}
        />
      </div>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {boxes.map((box, index) => (
          <div key={index} style={{ position: 'absolute', left: box.x, top: box.y + spacing.xxs }}>
            <SkeletonBlock
              width={Math.max(spacing.xxs, box.width)}
              height={Math.max(spacing.xxs, box.height - spacing.xs)}
              active={active}
              data-testid={id ? `${id}-line-${index}` : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
